import { Select } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuizData } from '../../hooks/useQuizData'
import styles from '../../styles/admin/StudyContent.module.scss'
import BaseInput from '../common/BaseInput'
import Label from '../common/Label'
import SaveButton from '../common/SaveButton'
import SaveResultModal from '../common/SaveResultModal'
import Table from '../common/Table'
import CurriculumHeader from './CurriculumHeader'
import CurriculumItem from './CurriculumItem'
import QuizContainer from './QuizContainer'
import QuizTypeA from './QuizTypeA'
import QuizTypeC from './QuizTypeC'
import { useQuizStore } from '../../store/useQuizStore'

const QuizContent = ({ data, questionId, fromPage,level }) => {
  const navigate = useNavigate()
  const existingQuestionImageUrl = useQuizStore(
    (state) => state.quizData.existingQuestionImageUrl
  )
  const existingSubQuestionImageUrl = useQuizStore(
    (state) => state.quizData.existingSubQuestionImageUrl
  )
  const existingExplanationImageUrl = useQuizStore(
    (state) => state.quizData.existingExplanationImageUrl
  )
  const {
    curriculum,
    quizData,
    isLoading,

    handleLevelChange,
    handleMainChapterChange,
    handleSubChapterChange,
    handleUnitChange,
    handleDetailUnitChange,
    handleCountChange,
    handleTypeChange,

    handleQuestionChange,
    handleQuestionImageChange,
    handleQuestionImage1Change,
    handleQuestionImage2Change,
    handleSubQuestionImageChange,
    handleCorrectAnswerChange,
    handleCorrectAnswersChange,
    handleCheckedStatesChange,
    handleAnswerImageChange,
    handleAnswerImageFilesChange,
    handleExplanationChange,
    handleExplanationImageChange,
    handleExistingQuestionImageRemove,
    handleExistingSubQuestionImageRemove,
    handleExistingExplanationImageRemove,
    handleSaveQuiz,
  } = useQuizData(data)

  const [opened, { open, close }] = useDisclosure(false)
  const [saveStatus, setSaveStatus] = useState(null)
  const [answerType, setAnswerType] = useState('text')

  useEffect(() => {
    if (!data) return   // data 로딩 전에 'text'로 덮어쓰지 않음
    const answerValues = [
      data.ANSWER1,
      data.ANSWER2,
      data.ANSWER3,
      data.ANSWER4,
      data.ANSWER5,
    ]
    const hasExistingImageAnswers = answerValues.some((answer) => {
      const value = String(answer || '').trim()
      return (
        value.startsWith('/') ||
        value.startsWith('http') ||
        value.includes('/uploads') ||
        /\.(png|jpe?g|gif|webp|svg)$/i.test(value)
      )
    })
    setAnswerType(hasExistingImageAnswers ? 'image' : 'text')
  }, [
    data?.ANSWER1,
    data?.ANSWER2,
    data?.ANSWER3,
    data?.ANSWER4,
    data?.ANSWER5,
  ])

  const singleAnswerQuizProps = useMemo(
    () => ({
      question: quizData.question,
      correctAnswer: quizData.correctAnswer,
      explanation: quizData.explanation,
      explanationImageFile: quizData.explanationImageFile,
      onQuestionChange: handleQuestionChange,
      onCorrectAnswerChange: handleCorrectAnswerChange,
      onExplanationChange: handleExplanationChange,
      onExplanationImageChange: handleExplanationImageChange,
    }),
    [
      quizData.question,
      quizData.correctAnswer,
      quizData.explanation,
      quizData.explanationImageFile,
      handleQuestionChange,
      handleCorrectAnswerChange,
      handleExplanationChange,
      handleExplanationImageChange,
    ]
  )

  const multipleAnswerQuizProps = useMemo(
    () => ({
      question: quizData.question,
      correctAnswers: quizData.correctAnswers,
      checkedStates: quizData.checkedStates,
      explanation: quizData.explanation,
      explanationImageFile: quizData.explanationImageFile,
      onQuestionChange: handleQuestionChange,
      onCorrectAnswersChange: handleCorrectAnswersChange,
      onCheckedStatesChange: handleCheckedStatesChange,
      onExplanationChange: handleExplanationChange,
      onExplanationImageChange: handleExplanationImageChange,
    }),
    [
      quizData.question,
      quizData.correctAnswers,
      quizData.checkedStates,
      quizData.explanation,
      quizData.explanationImageFile,
      handleQuestionChange,
      handleCorrectAnswersChange,
      handleCheckedStatesChange,
      handleExplanationChange,
      handleExplanationImageChange,
    ]
  )

  const curriculumData = useMemo(
    () => [
      {
        level: <span>{data?.LEVEL || ''}</span>,
        mainChapter: <span>{data?.MAIN || ''}</span>,
        subChapter: <span>{data?.SUB || ''}</span>,
        unit: <span>{data?.UNIT || ''}</span>,
        detailUnit: <span>{data?.DETAIL_UNIT || ''}</span>,
        no: (
          <BaseInput
            value={curriculum.no}
            placeholder={data?.NO?.toString() || '0'}
            onChange={(e) => handleCountChange(e.target.value)}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={3}
          />
        ),
        contentType: <Label text={data?.TYPE || 'Class'} />,
        questionLabel: (
          <span className={styles.questionLabelDisplay}>
            {curriculum.questionLabel || data?.DEPTH || ''}
          </span>
        ),
        type: (
          <Select
            placeholder={'Type'}
            data={['A', 'C']}
            value={data?.TYPE2}
            onChange={handleTypeChange}
          />
        ),
      },
    ],
    [
      data,
      curriculum.level,
      curriculum.mainChapter,
      curriculum.subChapter,
      curriculum.unit,
      curriculum.detailUnit,
      curriculum.no,
      curriculum.questionLabel,
      curriculum.selectedType,
      handleLevelChange,
      handleMainChapterChange,
      handleSubChapterChange,
      handleUnitChange,
      handleDetailUnitChange,
      handleCountChange,
      handleTypeChange,
    ]
  )

  const handleSaveWithModal = useCallback(async () => {
    try {
      await handleSaveQuiz()

      setSaveStatus({ type: 'success', message: 'Saved successfully' })
      open()
      setTimeout(() => {
        navigate('/lectures', { state: { fromPage, level } })
      }, 2000)
    } catch (error) {

      const errorMessage =
        error?.response?.data?.message || 'Failed to save. Please try again.'
      setSaveStatus({ type: 'error', message: errorMessage })
      open()
    }
  }, [
    handleSaveQuiz,
    open,
    navigate,
    fromPage,
    quizData,
    curriculum,
    data,
    questionId,
  ])

  const handleModalClose = useCallback(() => {
    close()
    setSaveStatus(null)
  }, [close])

  const handleModalConfirm = useCallback(() => {
    if (saveStatus?.type === 'success') {
      navigate('/lectures', { state: { fromPage } })
    } else {
      handleModalClose()
    }
  }, [saveStatus, navigate, handleModalClose, fromPage])

  const renderQuizTypeComponent = useMemo(() => {
    switch (data?.TYPE2) {
      case 'A':
        return (
          <QuizTypeA
            {...multipleAnswerQuizProps}
            questionImageFile={quizData.questionImageFile}
            subQuestionImageFile={quizData.subQuestionImageFile}
            answerImageFiles={quizData.answerImageFiles}
            onQuestionImageChange={handleQuestionImageChange}
            onSubQuestionImageChange={handleSubQuestionImageChange}
            onAnswerImageFilesChange={handleAnswerImageFilesChange}
            answerType={answerType}
            existingAnswerImageUrls={[
              data?.ANSWER1 || '',
              data?.ANSWER2 || '',
              data?.ANSWER3 || '',
              data?.ANSWER4 || '',
              data?.ANSWER5 || '',
            ]}
            existingQuestionImageUrl={
              existingQuestionImageUrl !== undefined
                ? existingQuestionImageUrl
                : data?.IMG_URL || ''
            }
            existingSubQuestionImageUrl={
              existingSubQuestionImageUrl !== undefined
                ? existingSubQuestionImageUrl
                : data?.IMG_URL2 || ''
            }
            existingExplanationImageUrl={
              existingExplanationImageUrl !== undefined
                ? existingExplanationImageUrl
                : data?.DESC_URL || ''
            }
            onExistingQuestionImageRemove={handleExistingQuestionImageRemove}
            onExistingSubQuestionImageRemove={
              handleExistingSubQuestionImageRemove
            }
            onExistingExplanationImageRemove={
              handleExistingExplanationImageRemove
            }
          />
        )

      case 'C':
        return (
          <QuizTypeC
            {...multipleAnswerQuizProps}
            questionImageFile={quizData.questionImageFile}
            onQuestionImageChange={handleQuestionImageChange}
            existingQuestionImageUrl={
              quizData.existingQuestionImageUrl !== undefined
                ? quizData.existingQuestionImageUrl
                : data?.IMG_URL || ''
            }
            existingExplanationImageUrl={
              quizData.existingExplanationImageUrl !== undefined
                ? quizData.existingExplanationImageUrl
                : data?.DESC_URL || ''
            }
            onExistingQuestionImageRemove={handleExistingQuestionImageRemove}
            onExistingExplanationImageRemove={
              handleExistingExplanationImageRemove
            }
          />
        )

      default:
        return (
          <QuizTypeA
            {...multipleAnswerQuizProps}
            questionImageFile={quizData.questionImageFile}
            subQuestionImageFile={quizData.subQuestionImageFile}
            onQuestionImageChange={handleQuestionImageChange}
            onSubQuestionImageChange={handleSubQuestionImageChange}
            existingQuestionImageUrl={
              quizData.existingQuestionImageUrl !== undefined
                ? quizData.existingQuestionImageUrl
                : data?.IMG_URL || ''
            }
            existingSubQuestionImageUrl={
              quizData.existingSubQuestionImageUrl !== undefined
                ? quizData.existingSubQuestionImageUrl
                : data?.IMG_URL2 || ''
            }
            existingExplanationImageUrl={
              quizData.existingExplanationImageUrl !== undefined
                ? quizData.existingExplanationImageUrl
                : data?.DESC_URL || ''
            }
            onExistingQuestionImageRemove={handleExistingQuestionImageRemove}
            onExistingSubQuestionImageRemove={
              handleExistingSubQuestionImageRemove
            }
            onExistingExplanationImageRemove={
              handleExistingExplanationImageRemove
            }
            answerType={answerType}
            answerImageFiles={quizData.answerImageFiles}
            onAnswerImageFilesChange={handleAnswerImageFilesChange}
          />
        )
    }
  }, [
    data?.TYPE2,
    multipleAnswerQuizProps,
    quizData.questionImageFile,
    quizData.subQuestionImageFile,
    quizData.answerImageFiles,
    quizData.existingQuestionImageUrl,
    quizData.existingSubQuestionImageUrl,
    quizData.existingExplanationImageUrl,
    handleQuestionImageChange,
    handleQuestionImage2Change,
    handleAnswerImageFilesChange,
    handleExistingQuestionImageRemove,
    handleExistingSubQuestionImageRemove,
    handleExistingExplanationImageRemove,
    answerType,
    data?.IMG_URL,
    data?.IMG_URL2,
    data?.DESC_URL,
  ])

  return (
    <QuizContainer>
      <Table className={styles.tableWrapper}>
        <thead>
          <CurriculumHeader />
        </thead>

        <tbody>
          {curriculumData.length > 0 ? (
            curriculumData.map((curriculumItem, index) => (
              <CurriculumItem
                key={index}
                curriculum={curriculumItem}
                index={index}
              />
            ))
          ) : (
            <tr>
              <Table.Cell colSpan={8}>
                <Table.Empty>No curriculum data available.</Table.Empty>
              </Table.Cell>
            </tr>
          )}
        </tbody>
      </Table>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '16px',
        }}
      >
        <p
          style={{
            color: '#111',
            fontSize: '15px',
            fontWeight: 700,
            margin: 0,
            marginLeft: '14px',
          }}
        >
          Answer Type:
        </p>
        <Select
          data={[
            { value: 'text', label: '텍스트' },
            { value: 'image', label: '이미지' },
          ]}
          value={answerType}
          onChange={(value) => setAnswerType(value)}
          className={styles.answerTypeSelector}
          w="100%"
        />
      </div>

      {renderQuizTypeComponent}

      <SaveButton
        className={styles.saveButtonWrapper}
        onClick={handleSaveWithModal}
        disabled={isLoading}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {isLoading ? 'Saving...' : 'Save'}
      </SaveButton>

      <SaveResultModal
        opened={opened}
        onClose={handleModalClose}
        status={saveStatus}
        onConfirm={handleModalConfirm}
      />
    </QuizContainer>
  )
}

export default QuizContent
